import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import moment from 'moment';
import API from '@/api/api'; // handleSubmit에서 PUT/POST에 사용
import { useApi } from '@/hooks/useApi';
import { Row, Col, Typography, Modal, Card, Divider, Table, Button, message, Tag } from 'antd';
import { Form, Input, Select, Radio, DatePicker } from 'antd';
import ManagingAdminLayout from '@layout/ManagingAdmin';
import { ProjectStatusCode, ProjectStatusCodeColor } from '@constants/status';

export default function MngProject() {
  const { data: projectsData, loading, error, refetch } = useApi('/admin/projects');
  const Projects = projectsData || []; // 데이터가 null일 경우 빈 배열로 초기화

  const [EditProject, setEditProject] = useState(null);
  const [ProjectPeriods, setProjectPeriods] = useState([]);
  const [ProjectTabActivePeriod, setProjectTabActivePeriod] = useState(null);
  const [isShowProjectFormModal, openShowProjectFormModal] = useState(false);
  const [form] = Form.useForm();
  const [ProjectFormDisabled, setProjectFormDisabled] = useState(false);

  // API 에러 처리
  useEffect(() => {
    if (error) {
      message.error('프로젝트 목록 조회 실패');
    }
  }, [error]);

  // 데이터 로딩 후 기간 탭 설정
  useEffect(() => {
    if (Projects.length) {
      const periods = [...new Set(Projects.map((p) => p.period))].map((p) => ({ key: p, tab: p }));
      setProjectPeriods(periods);
      setProjectTabActivePeriod((prev) => prev || periods[0]?.key);
    }
  }, [Projects]);

  // 모달이 열릴 때 폼 데이터 설정
  useEffect(() => {
    if (isShowProjectFormModal && EditProject) {
      const _Project = { ...EditProject };
      if (_Project.start_date) _Project.start_date = moment(_Project.start_date);
      if (_Project.end_date) _Project.end_date = moment(_Project.end_date);
      if (_Project.target_end_date) _Project.target_end_date = moment(_Project.target_end_date);
      form.setFieldsValue(_Project);

      if (_Project?.status_code && _Project.status_code === 'STARTED') {
        setProjectFormDisabled(true);
      } else {
        setProjectFormDisabled(false);
      }
    }
  }, [isShowProjectFormModal, EditProject, form, setProjectFormDisabled]);

  const handleSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        start_date: values.start_date ? dayjs(values.start_date).format('YYYY-MM-DD') : null,
        end_date: values.end_date ? dayjs(values.end_date).format('YYYY-MM-DD') : null,
        target_end_date: values.target_end_date
          ? dayjs(values.target_end_date).format('YYYY-MM-DD')
          : null,
      };

      if (payload?.period) {
        payload.period = String(payload.period);
      }
      if (EditProject?.id) {
        payload.id = EditProject.id;
        await API.put(`/admin/projects/${EditProject.id}`, payload);
        message.success('프로젝트가 성공적으로 수정되었습니다.');
      } else {
        await API.post('/admin/projects', payload);
        message.success('프로젝트가 성공적으로 생성되었습니다.');
      }
      openShowProjectFormModal(false);
      form.resetFields();
      refetch(); // API.get 대신 refetch() 호출
    } catch {
      message.error('프로젝트 저장 중 오류가 발생했습니다.');
    }
  };

  const ProjectCreate = () => (
    <Button
      onClick={() => {
        setEditProject(null);
        openShowProjectFormModal(true);
      }}
    >
      프로젝트 생성
    </Button>
  );

  const ProjectEdit = ({ project }) => (
    <Button
      onClick={() => {
        setEditProject(project);
        openShowProjectFormModal(true);
      }}
    >
      수정하기
    </Button>
  );

  const ProjectTable = (ActiveProjectPeriod) => {
    const columns = [
      { title: 'id', dataIndex: 'id', key: 'id' },
      { title: '프로젝트 명', dataIndex: 'project_name_ko', key: 'project_name_ko' },
      { title: '프로젝트 코드', dataIndex: 'project_code', key: 'project_code' },
      {
        title: '진행 상태',
        dataIndex: 'status_code',
        key: 'status_code',
        render: (status_code) => (
          <Tag color={ProjectStatusCodeColor[status_code]}> {ProjectStatusCode[status_code]} </Tag>
        ),
      },
      {
        title: '현재 참여기업',
        dataIndex: 'estimates',
        key: 'estimates',
        render: (estimates) => `${estimates.length} 개`,
      },
      { title: '수정', key: 'action', render: (_, record) => <ProjectEdit project={record} /> },
    ];
    const data = Projects.filter((p) => p.period === ActiveProjectPeriod);
    return <Table columns={columns} dataSource={data} rowKey="id" pagination={{ pageSize: 10 }} loading={loading} />;
  };

  return (
    <ManagingAdminLayout>
      <Typography.Title level={3}>프로젝트 관리</Typography.Title>
      <Modal
        open={isShowProjectFormModal}
        onCancel={() => {
          openShowProjectFormModal(false);
          form.resetFields();
        }}
        onOk={() => form.validateFields().then(handleSubmit)}
        title={EditProject?.id ? '프로젝트 수정' : '프로젝트 생성'}
        okText={EditProject?.id ? '수정 저장' : '생성하기'}
        destroyOnClose
        width="60%"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            id: null,
            project_name_ko: '신규 프로젝트',
            project_name_en: 'new project',
            project_name_zh: 'new project',
            project_code: 'new_project_code',
            period: '2025',
            require_scope12: true,
            require_waste: true,
            require_material: true,
            require_transport: true,
            status_code: 'NOT_STARTED',
            start_date: null,
            end_date: null,
            target_end_date: null,
            memo: null,
          }}
        >
          <Row gutter={[12, 12]}>
            <Col xs={24} lg={8}>
              <Form.Item
                name="project_name_ko"
                label="(국문) 프로젝트 이름"
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item name="project_name_en" label="(영문) 프로젝트 이름">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item name="project_name_zh" label="(중문) 프로젝트 이름">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item name="project_code" label="프로젝트 코드" rules={[{ required: true }]}>
                <Input disabled={ProjectFormDisabled} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item name="status_code" label="프로젝트 상태" rules={[{ required: true }]}>
                <Select
                  disabled={ProjectFormDisabled}
                  options={[
                    { label: '미시작', value: 'NOT_STARTED' },
                    { label: '시작', value: 'STARTED' },
                    { label: '종료', value: 'DONE' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={8}>
              <Form.Item name="period" label="Period" rules={[{ required: true }]}>
                <Input type="text" disabled={ProjectFormDisabled} />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Form.Item label="프로젝트 시작일" name="start_date" rules={[{ required: true }]}>
                <DatePicker disabled={ProjectFormDisabled} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="프로젝트 종료일" name="end_date" rules={[{ required: true }]}>
                <DatePicker />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="프로젝트 목표 종료일"
                name="target_end_date"
                rules={[{ required: true }]}
              >
                <DatePicker />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Row gutter={[12, 12]}>
            <Col xs={24}>
              <h3 className="fw-bold text-red-5">프로젝트별 증빙서류 설정기능 수정 필요</h3>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="scope12 필수유무"
                name="require_scope12"
                rules={[{ required: true }]}
              >
                <Radio.Group disabled>
                  <Radio value={true}>필수</Radio>
                  <Radio value={false}>비필수</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="waste 필수유무"
                name="require_waste"
                rules={[{ required: true }]}
                disabled
              >
                <Radio.Group disabled>
                  <Radio value={true}>필수</Radio>
                  <Radio value={false}>비필수</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="material 필수유무"
                name="require_material"
                rules={[{ required: true }]}
                disabled
              >
                <Radio.Group disabled>
                  <Radio value={true}>필수</Radio>
                  <Radio value={false}>비필수</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="transport 필수유무"
                name="require_transport"
                rules={[{ required: true }]}
                disabled
              >
                <Radio.Group disabled>
                  <Radio value={true}>필수</Radio>
                  <Radio value={false}>비필수</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Form.Item name="memo" label="메모">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Card
        title="프로젝트 리스트"
        extra={<ProjectCreate />}
        tabList={ProjectPeriods}
        activeTabKey={ProjectTabActivePeriod}
        onTabChange={setProjectTabActivePeriod}
      >
        {ProjectTabActivePeriod && ProjectTable(ProjectTabActivePeriod)}
      </Card>
    </ManagingAdminLayout>
  );
}